const API_BASE = import.meta.env.VITE_API_URL || 'https://shuliu-api.web3linkerclub.workers.dev';

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

class ApiClient {
  private token: string | null = null;

  constructor() {
    this.token = localStorage.getItem('token');
  }

  setToken(token: string) {
    this.token = token;
    localStorage.setItem('token', token);
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('token');
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
    });

    const data = await response.json();
    return data;
  }

  // 认证API
  async register(email: string, password: string, username: string) {
    return this.request<{ token: string; userId: number }>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ name: username, email, password }),
    });
  }

  async login(email: string, password: string) {
    return this.request<{ token: string; userId: number }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async getMe() {
    return this.request<any>('/api/auth/me');
  }

  // 书籍API
  async getBooks() {
    return this.request<any[]>('/api/books');
  }

  async uploadBook(formData: FormData) {
    const headers: HeadersInit = {};
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${API_BASE}/api/books/upload`, {
      method: 'POST',
      headers,
      body: formData,
    });

    return response.json();
  }

  async generateCards(bookId: number, extractedText: string) {
    return this.request<{ cardCount: number }>('/api/books/generate-cards', {
      method: 'POST',
      body: JSON.stringify({ bookId, extractedText }),
    });
  }

  async getBook(bookId: number) {
    return this.request<any>(`/api/books/${bookId}`);
  }

  async getBookProgress(bookId: number) {
    return this.request<{ progress: number; status: string }>(`/api/books/${bookId}/progress`);
  }

  async deleteBook(bookId: string | number) {
    return this.request(`/api/books/${bookId}`, { method: 'DELETE' });
  }

  // 卡片API
  async getCards() {
    return this.request<any[]>('/api/cards');
  }

  async getBookCards(bookId: number) {
    return this.request<any[]>(`/api/books/${bookId}/cards`);
  }

  async updateCardStatus(cardId: number, status: string) {
    return this.request(`/api/cards/${cardId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  }

  // 笔记API
  async getNotes() {
    return this.request<any[]>('/api/notes');
  }

  async createNote(cardId: number, content: string) {
    return this.request('/api/notes', {
      method: 'POST',
      body: JSON.stringify({ cardId, content }),
    });
  }

  // 收藏API
  async getFavorites() {
    return this.request<any[]>('/api/favorites');
  }

  async addFavorite(cardId: number) {
    return this.request('/api/favorites', {
      method: 'POST',
      body: JSON.stringify({ cardId }),
    });
  }

  // 学习统计API
  async recordStudy(cardId: number, duration: number) {
    return this.request('/api/stats/record', {
      method: 'POST',
      body: JSON.stringify({ cardId, duration }),
    });
  }
}

export const api = new ApiClient();
